import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import {
  ContainerType,
  RegistryTypes,
  PackageType,
  CommonTypes,
} from "@assets/types";
import RegistryService from "@services/RegistryService";
import { RootState } from "@state/store";

export const getAllCountries = createAsyncThunk(
  "registry/getAllCountries",
  () => RegistryService.getAllCountries()
);

export const getIncoterms = createAsyncThunk("registry/getIncoterms", () =>
  RegistryService.getIncoterms()
);

export const getContainerTypes = createAsyncThunk(
  "registry/getContainerTypes",
  () => RegistryService.getContainerTypes()
);

export const getPackageTypes = createAsyncThunk(
  "registry/getPackageTypes",
  () => RegistryService.getPackageTypes()
);

export const getHsCodes = createAsyncThunk("/commodities/hsCodes", () =>
  RegistryService.getHSCodes()
);

export interface RegistryState {
  incoterms: RegistryTypes.Incoterm[];
  containerTypes: ContainerType[];
  packageTypes: PackageType[];
  hsCodes: CommonTypes.HSCode[];
  countries: CommonTypes.Country[];
}

export const initialRegistryState: RegistryState = {
  containerTypes: [],
  hsCodes: [],
  incoterms: [],
  packageTypes: [],
  countries: [],
};

export const registrySlice = createSlice({
  name: "registry",
  initialState: initialRegistryState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getContainerTypes.rejected, (state) => {
        state.containerTypes = [];
      })
      .addCase(
        getContainerTypes.fulfilled,
        (state, action: PayloadAction<ContainerType[]>) => {
          state.containerTypes = action.payload;
        }
      )
      .addCase(getHsCodes.rejected, (state) => {
        state.hsCodes = [];
      })
      .addCase(
        getHsCodes.fulfilled,
        (state, action: PayloadAction<CommonTypes.HSCode[]>) => {
          state.hsCodes = action.payload;
        }
      )
      .addCase(getIncoterms.rejected, (state) => {
        state.incoterms = [];
      })
      .addCase(
        getIncoterms.fulfilled,
        (state, action: PayloadAction<RegistryTypes.Incoterm[]>) => {
          state.incoterms = action.payload;
        }
      )
      .addCase(getPackageTypes.rejected, (state) => {
        state.packageTypes = [];
      })
      .addCase(
        getPackageTypes.fulfilled,
        (state, action: PayloadAction<PackageType[]>) => {
          state.packageTypes = action.payload;
        }
      )
      .addCase(getAllCountries.rejected, (state) => {
        state.countries = [];
      })
      .addCase(
        getAllCountries.fulfilled,
        (state, action: PayloadAction<CommonTypes.Country[]>) => {
          state.countries = action.payload;
        }
      );
  },
});

export const registryState = (state: RootState) => state.registrySlice;

// for every slice, export the state - to be able to access each state on their own
export const incotermsState = (state: RootState) =>
  state.registrySlice.incoterms;

export const containerTypesState = (state: RootState) =>
  state.registrySlice.containerTypes;

export const packageTypesState = (state: RootState) =>
  state.registrySlice.packageTypes;

export const hsCodesState = (state: RootState) => state.registrySlice.hsCodes;

export const countriesState = (state: RootState) =>
  state.registrySlice.countries;

export default registrySlice.reducer;
